import * as UpstreamPrefab from "@prefab-cloud/prefab-cloud-react";
import { isNotNil } from "@vendrinc/frontend-ts";
import * as React from "react";
import * as Prefab from "~/domain/prefab";
import { useAnalytics } from "~/hooks/useAnalytics";
import { useTrackedExperiments } from "~/hooks/useTrackedExperiments";

export const usePrefab = () => {
  const prefab = UpstreamPrefab.usePrefab();
  const { isEnabled: isEnabledUpstream, get: getUpstream } = prefab;

  const { has: hasTrackedExperiment, add: addTrackedExperiment } =
    useTrackedExperiments();

  const { track } = useAnalytics();

  const trackExperimentIfNecessary = React.useCallback(
    (key: Prefab.FlagName, value: unknown) => {
      if (
        Prefab.isKnownExperiment(key) &&
        isNotNil(value) &&
        !hasTrackedExperiment(key, value)
      ) {
        addTrackedExperiment(key, value);

        track({
          type: "$experiment_started",
          properties: {
            "Experiment name": key,
            "Variant name": value?.toString() ?? "not-set",
          },
        });
      }
    },
    [addTrackedExperiment, hasTrackedExperiment, track],
  );

  const isEnabled = React.useCallback(
    (key: Prefab.FlagName) => {
      const value = isEnabledUpstream(key);
      trackExperimentIfNecessary(key, value);
      return value;
    },
    [isEnabledUpstream, trackExperimentIfNecessary],
  );

  const get = React.useCallback(
    <T extends Prefab.FlagName>(key: T): Prefab.Flags[T] | null | undefined => {
      const value = Prefab.decodeFlag(key, getUpstream(key));
      trackExperimentIfNecessary(key, value);
      return value;
    },
    [getUpstream, trackExperimentIfNecessary],
  );

  return {
    ...prefab,
    isEnabled,
    get,
  };
};
